import cn from 'classnames'
import { useSelector } from '../../../redux/hooks'
import { huddleUserIds } from '../../../redux/presence/selectors'
import Icon from '../Icon'
import styles from './AimStatus.module.scss'
import { formatAimStatusTooltip } from './aimStatusTooltip'
import { IAimStatusOptions, useAimStatus } from './useAimStatus'

export interface IProps extends IAimStatusOptions {
  userId: string
  className?: string
}

/**
 * Someone's status emoji, rendered beside their name.
 *
 * Beside the name rather than as a badge on the avatar, which is where this
 * started: avatars in the sidebar and in option lists are 20-24px, and a glyph
 * pinned to the corner of one is too small to read and covers the face doing it.
 * On the name line it sits at text size, in the reading order a status belongs
 * in — the same placement Slack uses, and the same one the members panel
 * already used before the rest of the app caught up.
 *
 * Nothing renders without an emoji: a status is optional, and the row must not
 * reserve space for one that is not there. The text and the expiry are in the
 * hover, which is the only place they fit. Someone in a huddle shows headphones.
 */
const AimStatusEmoji = ({ userId, status, statusSource, className }: IProps) => {
  const resolved = useAimStatus(userId, { status, statusSource })
  const isInHuddle = useSelector((state) => huddleUserIds(state).has(userId))
  // In a huddle, the headphones stand in for the status, as they do in Slack.
  if (isInHuddle) {
    return (
      <span
        className={cn(styles.statusEmoji, styles.huddle, className)}
        title="In a huddle"
        role="img"
        aria-label="In a huddle"
        data-cy="aim-status-huddle"
      >
        <Icon name="headphones" size="xs" />
      </span>
    )
  }
  if (!resolved?.emoji) return null

  const label = formatAimStatusTooltip(resolved)
  return (
    <span
      className={cn(styles.statusEmoji, className)}
      title={label}
      role="img"
      aria-label={label}
      data-cy="aim-status-emoji"
    >
      {resolved.emoji}
    </span>
  )
}

export default AimStatusEmoji
